import { logger } from '@pkg/logger';
import { VIMEO_URL_REGEX, YOUTUBE_URL_REGEX } from './types';

export enum VideoEmbedVariant {
  Inline = 'inline',
  Background = 'background',
}

export enum VideoProvider {
  Youtube = 'youtube',
  Vimeo = 'vimeo',
}

/** Identify the third-party player a video URL belongs to, `null` when unsupported. */
export const getVideoProvider = (videoUrl: string | undefined | null): VideoProvider | null => {
  if (!videoUrl) {
    return null;
  }

  if (YOUTUBE_URL_REGEX.test(videoUrl)) {
    return VideoProvider.Youtube;
  }

  if (VIMEO_URL_REGEX.test(videoUrl)) {
    return VideoProvider.Vimeo;
  }

  return null;
};

/** Domains a provider's player connects to, used to warm up connections on intent. */
export const VIDEO_PROVIDER_PRECONNECT_ORIGINS: Record<VideoProvider, string[]> = {
  [VideoProvider.Youtube]: ['https://www.youtube-nocookie.com', 'https://i.ytimg.com'],
  [VideoProvider.Vimeo]: ['https://player.vimeo.com', 'https://f.vimeocdn.com', 'https://i.vimeocdn.com'],
};

export const getYoutubeVideoId = (url: string) => {
  const match = url.match(YOUTUBE_URL_REGEX);

  if (match != null && match[6] != null) {
    return match[6];
  }

  return null;
};

interface VimeoVideoIds {
  vimeoVideoId: string;
  vimeoHashId?: string;
}

/** Video ID plus the optional hash ID that unlisted Vimeo videos require. */
export const getVimeoVideoIds = (url: string): VimeoVideoIds | null => {
  const match = url.match(VIMEO_URL_REGEX);

  if (match == null || match[1] == null) {
    return null;
  }

  return { vimeoVideoId: match[1], vimeoHashId: match[2] };
};

interface GetYoutubeEmbedUrlOptions {
  youtubeVideoId: string;
  variant?: VideoEmbedVariant;
  controls: boolean;
  autoplay: boolean;
}

export const getYoutubeEmbedUrl = ({ youtubeVideoId, variant = VideoEmbedVariant.Inline, controls, autoplay }: GetYoutubeEmbedUrlOptions) => {
  // Use youtube-nocookie.com for privacy-enhanced mode which bypasses some domain verification issues
  const url = `https://www.youtube-nocookie.com/embed/${youtubeVideoId}`;
  const params = new URLSearchParams({
    modestbranding: '1',
    playlist: youtubeVideoId,
    rel: '0',
    controls: (!!controls || controls === undefined) ? '1' : '0',
    audio: (!!controls || controls === undefined) ? '1' : '0',
    mute: (!!controls || controls === undefined) ? '0' : '1',
    autoplay: autoplay === undefined ? '0' : (autoplay ? '1' : '0'),
  });

  if (variant === 'background') {
    params.set('autoplay', !!controls ? '0' :( (!!autoplay || autoplay === undefined) ? '1' : '0')),
    params.set('audio', controls === undefined ? '0' : (controls ? '1' : '0'));
    params.set('mute', controls === undefined ? '1' : (controls ? '0' : '1'));
    params.set('controls', controls === undefined ? '0' : (controls ? '1' : '0'));
    params.set('loop', '1');
  }

  return `${url}?${params}`;
};

interface GetVimeoEmbedUrlOptions {
  vimeoVideoId: string;
  variant?: VideoEmbedVariant;
  vimeoHashId?: string;
  controls: boolean;
  autoplay: boolean;
}

export const getVimeoEmbedUrl = ({ vimeoVideoId, variant = VideoEmbedVariant.Inline, vimeoHashId, controls, autoplay }: GetVimeoEmbedUrlOptions) => {
  const url = `https://player.vimeo.com/video/${vimeoVideoId}`;
  let autoplayValue = !!controls ? 'false' : (autoplay !== undefined ? autoplay : 'false');
  const params = new URLSearchParams({
    title: 'false',
    autoplay: `${autoplayValue}`,
    byline: 'false',
    portrait: 'false',
    controls: `${controls !== undefined ? controls : 'true'}`,
    muted: `${controls !== undefined ? !controls : 'false'}`,
    keyboard: `${controls !== undefined ? controls : 'true'}`,
    background: `${controls !== undefined ? !controls : 'false'}`,
    dnt: '1',
  });

  if (vimeoHashId) {
    params.set('h', vimeoHashId);
  }

  if (variant === 'background') {
    let autoplayValue = !!controls ? 'false' : (autoplay !== undefined ? autoplay : 'true');
    params.set('autoplay', `${autoplayValue}`);
    params.set('background', `${controls !== undefined ? !controls : 'true'}`);
    params.set('muted', `${controls !== undefined ? !controls : 'true'}`);
    params.set('controls', `${controls !== undefined ? controls : 'false'}`);
    params.set('loop', 'true');
  }

  return withVimeoDoNotTrack(`${url}?${params}`);
};

/** Ensure Vimeo player embed URLs include Do Not Track (`dnt=1`). */
export function withVimeoDoNotTrack(embedUrl: string): string {
  if (!embedUrl) {
    return embedUrl;
  }

  try {
    const parsed = new URL(embedUrl);

    if (parsed.hostname !== 'player.vimeo.com') {
      return embedUrl;
    }

    parsed.searchParams.set('dnt', '1');
    return parsed.toString();
  } catch {
    return embedUrl;
  }
}

interface GetVideoEmbedUrlOptions {
  videoUrl: string;
  variant?: VideoEmbedVariant;
  controls: boolean;
  autoplay: boolean;
}

export const getVideoEmbedUrl = ({ videoUrl, variant, controls, autoplay }: GetVideoEmbedUrlOptions) => {
  if (YOUTUBE_URL_REGEX.test(videoUrl)) {
    const youtubeVideoId = getYoutubeVideoId(videoUrl);

    if (youtubeVideoId == null) {
      logger.error('Invalid Youtube URL', videoUrl);
      return '';
    }

    return getYoutubeEmbedUrl({ youtubeVideoId, variant, controls, autoplay });
  }

  if (VIMEO_URL_REGEX.test(videoUrl)) {
    const ids = getVimeoVideoIds(videoUrl);

    if (ids == null) {
      logger.error('Invalid Vimeo URL', videoUrl);
      return '';
    }

    return getVimeoEmbedUrl({ ...ids, variant, controls, autoplay });
  }

  logger.error('Invalid video URL - must be either a Youtube or Vimeo link', videoUrl);
  return '';
};

/**
 * Force autoplay on an embed URL that is injected in response to a user click,
 * so the click that dismisses a facade also starts playback.
 *
 * Applied on top of `getVideoEmbedUrl` rather than inside it, because the
 * autoplay/controls/mute combinations there describe the *authored* intent.
 */
export function withVideoAutoplay(embedUrl: string): string {
  if (!embedUrl) {
    return embedUrl;
  }

  try {
    const parsed = new URL(embedUrl);

    if (parsed.hostname === 'player.vimeo.com') {
      parsed.searchParams.set('autoplay', 'true');
    } else if (parsed.hostname.endsWith('youtube.com') || parsed.hostname.endsWith('youtube-nocookie.com')) {
      parsed.searchParams.set('autoplay', '1');
    } else {
      return embedUrl;
    }

    return parsed.toString();
  } catch {
    return embedUrl;
  }
}

/**
 * Ordered YouTube thumbnail qualities. `maxresdefault` only exists for videos
 * uploaded in HD, so callers fall back to the next entry when it 404s.
 */
export const YOUTUBE_THUMBNAIL_QUALITIES = ['maxresdefault', 'hqdefault'] as const;

export type YoutubeThumbnailQuality = (typeof YOUTUBE_THUMBNAIL_QUALITIES)[number];

/**
 * Static YouTube thumbnail URL — no API call needed.
 *
 * Served from `i.ytimg.com`, the same asset as `img.youtube.com/vi/...` but on the
 * image CDN host that our CSP `img-src` already allows (`https://*.ytimg.com`).
 */
export const getYoutubeThumbnailUrl = (
  youtubeVideoId: string,
  quality: YoutubeThumbnailQuality = 'maxresdefault',
) => `https://i.ytimg.com/vi/${youtubeVideoId}/${quality}.jpg`;

/** Next-best thumbnail quality after `quality`, or `null` when exhausted. */
export const getNextYoutubeThumbnailQuality = (
  quality: YoutubeThumbnailQuality,
): YoutubeThumbnailQuality | null =>
  YOUTUBE_THUMBNAIL_QUALITIES[YOUTUBE_THUMBNAIL_QUALITIES.indexOf(quality) + 1] ?? null;

interface GetVimeoOembedUrlOptions {
  videoUrl: string;
  width?: number;
}

/**
 * Vimeo exposes no static thumbnail URL scheme, so the preview image has to be
 * looked up through oEmbed. Do Not Track is requested for the lookup as well.
 */
export const getVimeoOembedUrl = ({ videoUrl, width = 1280 }: GetVimeoOembedUrlOptions) => {
  const params = new URLSearchParams({ url: videoUrl, width: `${width}`, dnt: '1' });

  return `https://vimeo.com/api/oembed.json?${params}`;
};

interface FetchVimeoThumbnailOptions {
  videoUrl: string;
  width?: number;
  signal?: AbortSignal;
}

/**
 * Resolve a Vimeo preview image via oEmbed. Returns `null` on any failure so
 * callers can fall back to a CMS-authored cover image.
 *
 * This contacts Vimeo, so callers that gate Vimeo embeds must not call it before
 * that gate opens.
 */
export async function fetchVimeoThumbnailUrl({
  videoUrl,
  width,
  signal,
}: FetchVimeoThumbnailOptions): Promise<string | null> {
  if (!videoUrl || getVideoProvider(videoUrl) !== VideoProvider.Vimeo) {
    return null;
  }

  try {
    const response = await fetch(getVimeoOembedUrl({ videoUrl, width }), { signal });

    if (!response.ok) {
      logger.error('Failed to fetch Vimeo thumbnail metadata', videoUrl, response.status);
      return null;
    }

    const data = (await response.json()) as { thumbnail_url?: string };

    return data.thumbnail_url ?? null;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }

    logger.error('Failed to fetch Vimeo thumbnail metadata', videoUrl, error);
    return null;
  }
}
